import Axios from '@/lib/axiosConfig';
import axios from 'axios';

// Only raster images can be downscaled → webp in the browser. Everything else
// (svg, gif, pdf, video, docs) is uploaded as-is with no thumbnail.
const THUMBNAILABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const THUMBNAIL_MAX_WIDTH = 800;
const THUMBNAIL_QUALITY = 0.75;

/**
 * Build a max-800px-wide WebP copy of an image in the browser.
 * Small images are NOT upscaled. Returns null for non-raster types, if webp
 * encoding isn't supported, or if decoding fails.
 */
export async function createWebpThumbnail(file: File): Promise<Blob | null> {
  if (!THUMBNAILABLE_TYPES.includes(file.type)) return null;
  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: 'from-image', // honor EXIF rotation
    });
    const scale = Math.min(1, THUMBNAIL_MAX_WIDTH / bitmap.width); // never upscale
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/webp', THUMBNAIL_QUALITY)
    );
    return blob && blob.type === 'image/webp' ? blob : null;
  } catch {
    return null;
  }
}

interface PresignedResponse {
  url: string;
  key: string;
  thumbnail?: { url: string; key: string };
}

/**
 * Upload an image — and, for raster types, its 800px WebP thumbnail — to S3 in
 * a single presigned call. Returns the original's permanent URL/key (the
 * thumbnail lands at thumbnails/<purpose>/<file>.webp and is derived on read).
 *
 * Non-hook counterpart of `useImageUpload`, for module-level upload helpers.
 */
export async function uploadImageWithThumbnail(
  file: File,
  purpose: string
): Promise<string> {
  const thumbnail = await createWebpThumbnail(file);

  const query =
    `/presigned-url?purpose=${purpose}` +
    `&fileName=${encodeURIComponent(file.name)}` +
    `&fileType=${encodeURIComponent(file.type)}` +
    (thumbnail ? `&thumbnail=true` : ``);

  const { data } = await Axios.get(query);
  const { url, key, thumbnail: thumb } =
    data.responseObject as PresignedResponse;

  // Content-Type on each PUT must match what was signed or S3 returns 403.
  await Promise.all([
    axios.put(url, file, { headers: { 'Content-Type': file.type } }),
    thumbnail && thumb?.url
      ? axios.put(thumb.url, thumbnail, {
          headers: { 'Content-Type': 'image/webp' },
        })
      : Promise.resolve(),
  ]);

  return key;
}
